import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Skill, UserSkill, UserSkillHistory, UpdateUserSkillLevelPayload} from '@mira-avant/mtlibs';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class SkillService {
	private readonly BASE_PATH = 'skills';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public skills$: BehaviorSubject<Skill[]> = new BehaviorSubject<Skill[]>([]);
	public userSkills$: BehaviorSubject<UserSkill[]> = new BehaviorSubject<UserSkill[]>([]);
	public userSkillHistory$: BehaviorSubject<UserSkillHistory[]> = new BehaviorSubject<UserSkillHistory[]>([]);

	constructor(private api: ApiService) {
		// Load skills on init
		this.getSkills$().catch(error => {
			console.error('Error fetching skills:', error);
			this.skills$.next([]);
		});
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async getSkills$(): Promise<Skill[]> {
		return this.withLoading(async () => {
			const skills = await firstValueFrom(this.api.get<Skill[]>(this.BASE_PATH + '/all'));
			this.skills$.next(skills);
			return skills;
		});
	}

	public async getUserSkillLevel$(cognitoUuid: string): Promise<UserSkill[]> {
		return this.withLoading(async () => {
			const userSkills = await firstValueFrom(this.api.get<UserSkill[]>(`${this.BASE_PATH}/user/${cognitoUuid}`));
			this.userSkills$.next(userSkills);
			return userSkills;
		});
	}

	public async getUserSkillHistory$(cognitoUuid: string): Promise<UserSkillHistory[]> {
		return this.withLoading(async () => {
			const userSkillHistory = await firstValueFrom(
				this.api.get<UserSkillHistory[]>(`${this.BASE_PATH}/history/${cognitoUuid}`)
			);
			this.userSkillHistory$.next(userSkillHistory);
			return userSkillHistory;
		});
	}

	public async updateUserSkillLevel$(
		cognitoUuid: string,
		skillCode: string,
		payload: UpdateUserSkillLevelPayload
	): Promise<UserSkill> {
		return this.withLoading(async () => {
			// Validate payload
			if (
				!payload.rubric_code ||
				!payload.course_uuid ||
				payload.rubric_level_level === null ||
				payload.rubric_level_level === undefined
			) {
				throw new Error('rubric_code, course_uuid, and rubric_level_level are required');
			}

			const updatedUserSkill = await firstValueFrom(
				this.api.put<UserSkill>(`${this.BASE_PATH}/user/${cognitoUuid}/skill/${skillCode}`, payload)
			);

			const currentUserSkills = this.userSkills$.value;

			// Find existing skill by user_uuid, course_uuid, rubric_code, and skill_code
			const existingSkillIndex = currentUserSkills.findIndex(
				us =>
					us.user_uuid === updatedUserSkill.user_uuid &&
					us.course_uuid === updatedUserSkill.course_uuid &&
					us.rubric_code === updatedUserSkill.rubric_code &&
					us.skill_code === updatedUserSkill.skill_code
			);

			if (existingSkillIndex !== -1) {
				currentUserSkills[existingSkillIndex] = updatedUserSkill;
			} else {
				currentUserSkills.push(updatedUserSkill);
			}

			this.userSkills$.next(currentUserSkills);

			return updatedUserSkill;
		});
	}
}
